import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, LogOut, ArrowRight, CheckCircle2, Circle, User as UserIcon, Star,
  Layers, ChevronLeft, Search, Save, Music4
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });
const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const sessionKey = (t) => `sc_judge_${t}`;
function loadSession(token) {
  try {
    const s = JSON.parse(localStorage.getItem(sessionKey(token)) || "null");
    if (s && s.judge_id && s.judge_name) return s;
  } catch { /* ignore */ }
  return null;
}
function saveSession(token, s) { localStorage.setItem(sessionKey(token), JSON.stringify(s)); }
function clearSession(token) { localStorage.removeItem(sessionKey(token)); }

export default function JudgePortal() {
  const { token } = useParams();
  const [preview, setPreview] = useState(null);
  const [linkErr, setLinkErr] = useState("");
  const [session, setSession] = useState(() => loadSession(token));
  const [nameForm, setNameForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [payload, setPayload] = useState(null);            // {rounds, items, judge_name, production_title}
  const [selectedRound, setSelectedRound] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  // Fetch preview on load
  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get("/judging/preview", { params: { token } });
        setPreview(data);
      } catch (e) {
        setLinkErr(formatApiErrorDetail(e.response?.data?.detail) || "This link is invalid or has been revoked.");
      }
    })();
  }, [token]);

  const loadAuditions = useCallback(async (round_id) => {
    if (!session) return;
    try {
      const { data } = await publicApi.get("/judging/auditions", {
        params: { token, judge_id: session.judge_id, round_id: round_id || undefined },
      });
      setPayload(data);
    } catch (e) {
      // If judge_id no longer valid, clear session
      if (e.response?.status === 403) { clearSession(token); setSession(null); }
    }
  }, [session, token]);

  useEffect(() => { if (session && preview) loadAuditions(selectedRound); }, [session, preview, selectedRound, loadAuditions]);

  const startSession = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data } = await publicApi.post("/judging/session", { token, ...nameForm });
      const s = { judge_id: data.judge_id, judge_name: data.judge_name };
      saveSession(token, s);
      setSession(s);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not start session");
    } finally { setSubmitting(false); }
  };

  const signOut = () => {
    clearSession(token);
    setSession(null); setPayload(null); setSelected(null); setNameForm({ name: "", email: "" });
  };

  const filtered = useMemo(() => {
    if (!payload) return [];
    const term = q.toLowerCase().trim();
    if (!term) return payload.items;
    return payload.items.filter((a) =>
      a.name.toLowerCase().includes(term) ||
      (a.interested_roles || []).some((r) => r.toLowerCase().includes(term))
    );
  }, [payload, q]);

  // ---- Error / invalid link ----
  if (linkErr) {
    return (
      <ShellCentered>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Judging link</div>
        <h1 className="mt-2 font-display text-4xl">Link unavailable.</h1>
        <p className="mt-3 text-zinc-400">{linkErr}</p>
        <p className="mt-2 text-xs text-zinc-500">Ask the producer for a fresh link.</p>
      </ShellCentered>
    );
  }

  // ---- Loading preview ----
  if (!preview) {
    return <ShellCentered><div className="text-sm text-zinc-500">Loading…</div></ShellCentered>;
  }

  // ---- Name entry ----
  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <form onSubmit={startSession} className="w-full max-w-md space-y-6 animate-fade-up" data-testid="judge-name-form">
          <div className="flex items-center gap-3">
            {preview.production?.theater_logo_url ? (
              <img src={preview.production.theater_logo_url} alt={preview.production.theater_name || "Theatre"} className="w-10 h-10 rounded-md object-contain bg-[#121212] border border-white/10 p-0.5" />
            ) : (
              <Sparkles className="w-5 h-5 text-gold" />
            )}
            <span className="font-display text-2xl">{preview.production?.theater_name || "StageCall"}</span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.28em] text-zinc-500">Adjudicator</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">You&apos;re judging</div>
            <h1 className="mt-1 font-display text-4xl leading-tight" data-testid="judge-production-title">{preview.production.title}</h1>
            {preview.production.director && <div className="mt-1 text-zinc-400 text-sm">Directed by {preview.production.director}</div>}
          </div>
          <p className="text-sm text-zinc-400">Enter your name to begin — your scores will be attributed to you. You&apos;ll only be asked once on this device.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Your name *</Label>
              <Input data-testid="judge-name-input" required autoFocus value={nameForm.name} onChange={(e) => setNameForm({ ...nameForm, name: e.target.value })}
                className="mt-2 bg-[#121212] border-white/10 h-12 focus:border-gold focus:ring-1 focus:ring-gold" placeholder="Jane Smith" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Email (optional)</Label>
              <Input data-testid="judge-email-input" type="email" value={nameForm.email} onChange={(e) => setNameForm({ ...nameForm, email: e.target.value })}
                className="mt-2 bg-[#121212] border-white/10 h-12" placeholder="you@example.com" />
            </div>
          </div>
          {error && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">{error}</div>}
          <button type="submit" disabled={submitting} data-testid="judge-continue-btn" style={goldBtn} className="w-full h-11 rounded-md font-semibold inline-flex items-center justify-center gap-1 hover:brightness-110">
            {submitting ? "Starting…" : (<>Start judging <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    );
  }

  // ---- Detail view ----
  if (selected) {
    return (
      <JudgeAuditionDetail
        audition={selected}
        token={token}
        judgeId={session.judge_id}
        onBack={() => { setSelected(null); loadAuditions(selectedRound); }}
      />
    );
  }

  // ---- List ----
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="judge-portal">
      <header className="sticky top-0 z-20 backdrop-blur bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center gap-4 px-6 py-3">
          <div className="flex items-center gap-2">
            {preview.production?.theater_logo_url ? (
              <img src={preview.production.theater_logo_url} alt={preview.production.theater_name || "Theatre"} className="w-8 h-8 rounded object-contain bg-[#121212] border border-white/10 p-0.5" />
            ) : (
              <Sparkles className="w-5 h-5 text-gold" />
            )}
            <span className="font-display text-xl">{preview.production?.theater_name || "StageCall"}</span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.28em] text-zinc-500">Adjudicator</span>
          </div>
          <div className="hidden md:block h-6 w-px bg-white/10" />
          <div className="hidden md:block">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{preview.production.title}</div>
            <div className="text-sm text-zinc-300">Judging as <span className="text-white font-medium" data-testid="judge-name-badge">{session.judge_name}</span></div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="judge-signout" onClick={signOut} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1 h-9 px-3 rounded border border-white/10 hover:bg-white/5">
              <LogOut className="w-3.5 h-3.5" /> Not me
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {payload?.rounds?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 p-1 rounded-lg bg-[#121212] border border-white/10 w-fit" data-testid="judge-rounds-bar">
            <button onClick={() => setSelectedRound(null)} data-testid="judge-round-all"
              className={`h-8 px-3 text-xs uppercase tracking-widest rounded transition-colors ${!selectedRound ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
              All
            </button>
            {payload.rounds.map((r) => (
              <button key={r.id} onClick={() => setSelectedRound(r.id)} data-testid={`judge-round-${r.id}`}
                className={`h-8 px-3 text-xs uppercase tracking-widest rounded transition-colors inline-flex items-center gap-1
                  ${selectedRound === r.id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
                <Layers className="w-3 h-3" /> {r.name}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or role…" className="pl-9 bg-[#121212] border-white/10 h-10" data-testid="judge-search" />
          </div>
          {payload && (
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">
              {payload.items.filter((a) => a.scored).length} / {payload.items.length} scored
            </div>
          )}
        </div>

        {!payload ? (
          <div className="text-sm text-zinc-500">Loading auditions…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 p-12 text-center bg-[#0F0F0F]">
            <UserIcon className="w-10 h-10 mx-auto text-zinc-600" />
            <div className="mt-3 font-display text-lg">Nothing to judge yet</div>
            <div className="mt-1 text-sm text-zinc-500">{payload.items.length === 0 ? "The production team hasn't received any submissions yet." : "No matches for that search."}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map((a) => (
              <button key={a.id} onClick={() => setSelected(a)} data-testid={`judge-card-${a.id}`}
                className="text-left group rounded-lg border border-white/10 bg-[#121212] hover:border-gold/40 hover:-translate-y-0.5 transition-all overflow-hidden">
                <div className="aspect-[4/5] bg-[#0A0A0A] overflow-hidden relative">
                  {a.headshot_url ? (
                    <img src={a.headshot_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-zinc-700"><UserIcon className="w-12 h-12" /></div>
                  )}
                  {a.round_name && <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-black/60 text-zinc-200 border border-white/15">{a.round_name}</div>}
                  {a.scored ? (
                    <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Scored
                    </div>
                  ) : (
                    <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-black/60 text-zinc-400 border border-white/15 inline-flex items-center gap-1">
                      <Circle className="w-3 h-3" /> To do
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-display truncate">{a.name}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(a.interested_roles || []).slice(0, 2).map((r) => (
                      <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/10 truncate max-w-[9rem]">{r}</span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function JudgeAuditionDetail({ audition, token, judgeId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ acting_score: null, vocal_score: null, dance_score: null, comment: "", suggested_roles: [] });
  const [suggestQuery, setSuggestQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await publicApi.get(`/judging/auditions/${audition.id}`, { params: { token, judge_id: judgeId } });
      setDetail(data);
      const my = data.my_score;
      setForm({
        acting_score: my?.acting_score ?? null,
        vocal_score: my?.vocal_score ?? null,
        dance_score: my?.dance_score ?? null,
        comment: my?.comment || "",
        suggested_roles: my?.suggested_roles || [],
      });
    })();
  }, [audition.id, token, judgeId]);

  const addSuggested = () => {
    const v = suggestQuery.trim();
    if (!v || form.suggested_roles.includes(v)) return;
    setForm((f) => ({ ...f, suggested_roles: [...f.suggested_roles, v] })); setSuggestQuery("");
  };

  const save = async () => {
    setSaving(true); setSaved(false); setSaveErr("");
    try {
      await publicApi.post(`/judging/auditions/${audition.id}/score`, { token, judge_id: judgeId, ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not save. Try again.");
    }
    finally { setSaving(false); }
  };

  if (!detail) return <ShellCentered><div className="text-sm text-zinc-500">Loading…</div></ShellCentered>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="judge-detail">
      <header className="sticky top-0 z-20 backdrop-blur bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-5xl mx-auto flex items-center gap-3 px-6 py-3">
          <button onClick={onBack} data-testid="judge-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white h-9 px-3 rounded border border-white/10 hover:bg-white/5">
            <ChevronLeft className="w-4 h-4" /> Back to list
          </button>
          <div className="ml-auto text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            {saved && <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Saved</span>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Left: profile */}
          <div className="md:col-span-3 space-y-6">
            <div className="rounded-lg border border-white/10 bg-[#121212] overflow-hidden">
              <div className="aspect-[16/10] bg-[#0A0A0A] overflow-hidden">
                {detail.headshot_url ? (
                  <img src={detail.headshot_url} alt={detail.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-700"><UserIcon className="w-16 h-16" /></div>
                )}
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">{detail.round_name || "Audition"}</div>
                <h1 className="mt-1 font-display text-3xl md:text-4xl leading-tight">{detail.name}</h1>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-400">
                  {detail.age && <span>Age {detail.age}</span>}
                  {detail.height && <span>{detail.height}</span>}
                  {detail.vocal_range && <span className="inline-flex items-center gap-1"><Music4 className="w-3.5 h-3.5" />{detail.vocal_range}</span>}
                </div>
              </div>
            </div>

            <ProfileSection title="Interested in">
              {(detail.interested_roles || []).length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {detail.interested_roles.map((r) => <span key={r} className="text-[11px] px-2 py-1 rounded border border-gold/30 bg-gold/5 text-gold">{r}</span>)}
                </div>
              ) : <div className="text-zinc-500 text-sm">Not specified</div>}
            </ProfileSection>

            <ProfileSection title="About">
              <FieldLine label="Dance experience" value={detail.dance_experience} />
              <FieldLine label="Special skills" value={detail.special_skills} />
              <FieldLine label="Previous experience" value={detail.previous_experience} multiline />
            </ProfileSection>

            <ProfileSection title="Availability">
              <FieldLine label="From" value={detail.availability_start} />
              <FieldLine label="Until" value={detail.availability_end} />
            </ProfileSection>

            {detail.conflicts?.length > 0 && (
              <ProfileSection title={`Conflicts (${detail.conflicts.length})`}>
                <ul className="space-y-1 text-sm">
                  {detail.conflicts.map((c, i) => (
                    <li key={i} className="text-zinc-300">
                      <span className="text-zinc-500">{fmtDate(c.date)}</span>{c.start_time && ` · ${fmtTime(c.start_time)}–${c.end_time ? fmtTime(c.end_time) : "?"}`}
                      {c.reason && <span className="text-zinc-500"> — {c.reason}</span>}
                    </li>
                  ))}
                </ul>
              </ProfileSection>
            )}
          </div>

          {/* Right: scoring */}
          <div className="md:col-span-2">
            <div className="md:sticky md:top-20 rounded-lg border border-gold/30 bg-gold/5 p-5 space-y-4" data-testid="judge-score-panel">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> Your score</div>
                <div className="text-[11px] text-zinc-500 mt-1">1 (poor) to 10 (excellent). Leave blank to skip.</div>
              </div>

              <ScoreRow label="Acting" testid="jscore-acting" value={form.acting_score} onChange={(v) => setForm((f) => ({ ...f, acting_score: v }))} />
              <ScoreRow label="Vocal" testid="jscore-vocal" value={form.vocal_score} onChange={(v) => setForm((f) => ({ ...f, vocal_score: v }))} />
              <ScoreRow label="Dance" testid="jscore-dance" value={form.dance_score} onChange={(v) => setForm((f) => ({ ...f, dance_score: v }))} />

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Notes</Label>
                <Textarea data-testid="jscore-comment" rows={5} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  className="mt-1 bg-[#0A0A0A] border-white/10" placeholder="Impressions, callbacks, cast suggestions…" />
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Suggested roles</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Input value={suggestQuery} onChange={(e) => setSuggestQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSuggested(); } }}
                    placeholder="Role name…" className="bg-[#0A0A0A] border-white/10 h-9 text-sm" />
                  <button onClick={addSuggested} className="h-9 px-2 rounded border border-white/15 text-xs">Add</button>
                </div>
                {form.suggested_roles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {form.suggested_roles.map((r) => (
                      <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30">
                        {r}<button onClick={() => setForm((f) => ({ ...f, suggested_roles: f.suggested_roles.filter((x) => x !== r) }))} className="ml-1 text-gold/70 hover:text-gold">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={save} disabled={saving} data-testid="judge-save-btn" style={goldBtn} className="w-full h-11 rounded font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : (detail.my_score ? "Update your score" : "Save your score")}
              </button>
              {saveErr && <div data-testid="judge-save-err" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2 mt-2">{saveErr}</div>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function ShellCentered({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center space-y-3 animate-fade-up">
        <div className="flex items-center justify-center gap-2 opacity-90">
          <Sparkles className="w-5 h-5 text-gold" /><span className="font-display text-2xl">StageCall</span>
          <span className="ml-2 text-[10px] uppercase tracking-[0.28em] text-zinc-500">Adjudicator</span>
        </div>
        {children}
        <Link to="/login" className="mt-4 inline-block text-xs text-zinc-500 hover:text-white">Back to StageCall</Link>
      </div>
    </div>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">{title}</div>
      <div className="rounded-lg border border-white/10 bg-[#121212] p-4 space-y-2">{children}</div>
    </div>
  );
}
function FieldLine({ label, value, multiline }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 text-sm">
      <div className="w-32 shrink-0 text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`flex-1 text-zinc-200 ${multiline ? "whitespace-pre-wrap" : ""}`}>{value}</div>
    </div>
  );
}
function ScoreRow({ label, testid, value, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400">{label}</span>
        <span className="text-xs font-mono text-gold">{value ?? "—"}</span>
      </div>
      <div className="flex items-center gap-1">
        {[1,2,3,4,5,6,7,8,9,10].map((n) => (
          <button key={n} type="button" onClick={() => onChange(value === n ? null : n)} data-testid={`${testid}-${n}`}
            className={`flex-1 h-9 text-[11px] font-mono rounded border transition-colors
              ${value === n ? "bg-gold text-black border-gold font-bold" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}
